import { ActionFunctionArgs } from "@remix-run/node"
import { Form, Link, useActionData, useNavigation } from "@remix-run/react"
import { z } from "zod"
import { Button } from "~/components/ui/button"
import { Input } from "~/components/ui/input"
import { Label } from "~/components/ui/label"
import { FormValidationMessage, ValidationMessage } from "~/components/ui/validation-message"
import { signup } from "~/lib/auth"
import { getFieldErrors, parseIssues, phoneRegex } from "~/lib/utils"

const signupSchema = z
    .object({
        email: z.string().min(1, { message: "This field is required." }).email("This is not a valid email."),
        password: z.string().min(6, { message: "Password must be at least 6 characters" }),
        confirmPassword: z.string().min(6, { message: "Password must be at least 6 characters" }),
        companyId: z.string().min(1, { message: "This field is required." }),
        invoiceId: z.string().min(1, { message: "This field is required." }),
        firstName: z.string().min(1, { message: "This field is required." }),
        lastName: z.string().min(1, { message: "This field is required." }),
        phone: z.string().min(1, { message: "This field is required." }).regex(phoneRegex, "Invalid phone number."),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords don't match",
        path: ["confirmPassword"],
    })

export async function action({ request }: ActionFunctionArgs) {
    const formData = await request.formData()
    const validation = signupSchema.safeParse(Object.fromEntries(formData.entries()))

    if (!validation.success) {
        return {
            status: "error",
            type: "validation",
            message: "Form did not pass validation.",
            errors: parseIssues(validation.error.issues),
        }
    }

    // will redirect if a valid login
    const result = await signup({
        email: validation.data.email,
        password: validation.data.password,
        companyId: validation.data.companyId,
        invoiceId: validation.data.invoiceId,
        firstName: validation.data.firstName,
        lastName: validation.data.lastName,
        phone: validation.data.phone,
    })

    return {
        status: "error",
        type: "server",
        message: result.message,
        errors: [],
    }
}

export default function Signup() {
    const actionData = useActionData<typeof action>()
    const navigation = useNavigation()

    return (
        <div>
            <div className="text-center mb-6">
                <h1 className="text-2xl font-bold mb-1">Create account</h1>
                <p className="text-sm font-medium text-gray-400">Fill in your details to create an account</p>
            </div>
            <Form method="post" className="space-y-4 mb-6">
                {actionData && actionData.status == "error" && actionData.type == "server" && (
                    <FormValidationMessage variant="error" message={actionData.message} />
                )}
                <div className="space-y-2">
                    <Label htmlFor="email" isRequired>
                        Email
                    </Label>
                    <Input name="email" />
                    <ValidationMessage message={getFieldErrors(actionData?.errors, "email")} />
                </div>
                <div className=" grid grid-cols-2 gap-2">
                    <div>
                        <Label htmlFor="firstName" isRequired>
                            First name
                        </Label>
                        <Input name="firstName" />
                        <ValidationMessage message={getFieldErrors(actionData?.errors, "firstName")} />
                    </div>
                    <div>
                        <Label htmlFor="lastName" isRequired>
                            Last name
                        </Label>
                        <Input name="lastName" />
                        <ValidationMessage message={getFieldErrors(actionData?.errors, "lastName")} />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="phone" isRequired>
                        Phone
                    </Label>
                    <Input name="phone" />
                    <ValidationMessage message={getFieldErrors(actionData?.errors, "phone")} />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="password" isRequired>
                        Password
                    </Label>
                    <Input name="password" type="password" />
                    <ValidationMessage message={getFieldErrors(actionData?.errors, "password")} />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="confirmPassword" isRequired>
                        Confirm password
                    </Label>
                    <Input name="confirmPassword" type="password" />
                    <ValidationMessage message={getFieldErrors(actionData?.errors, "confirmPassword")} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <Label htmlFor="companyId" isRequired>
                            Company ID
                        </Label>
                        <Input name="companyId" />
                        <ValidationMessage message={getFieldErrors(actionData?.errors, "companyId")} />
                    </div>
                    <div>
                        <Label htmlFor="invoiceId" isRequired>
                            Invoice ID
                        </Label>
                        <Input name="invoiceId" />
                        <ValidationMessage message={getFieldErrors(actionData?.errors, "invoiceId")} />
                    </div>
                </div>
                <div>
                    <Button type="submit" className="mt-2 w-full" isLoading={navigation.state === "submitting"}>
                        Signup
                    </Button>
                </div>
            </Form>
            <p className="text-[0.8rem] text-center font-medium text-primary">
                <Link to={"/login"}>Already have an account?</Link>
            </p>
        </div>
    )
}
